import { Button, Col, Pagination, Row, Typography } from 'antd';
import { PaginationProps } from 'antd/lib';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import CollapsableFilter from '../../../../shared/components/collapsable-filter/collapsable-filter';
import { columnsSelectorModalId } from '../../../../shared/components/columns-selector/constants/constants';
import FormActions from '../../../../shared/components/form-actions/form-actions';
import PageTitle from '../../../../shared/components/page-title/page-title';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import useFormWithSearchParams from '../../../../shared/hooks/use-form-with-search-params';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import { ColumnsIcon } from '../../../../shared/icons/columns-icon';
import { useAppSelector } from '../../../../store/hooks';
import { TemplateField } from '../../../templates/models/template-field';
import { openModal } from '../../../ui/store/ui-slice';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import {
  signRequestArchiveModalId,
  signRequestFilterFormId,
  signRequestsExportModalId,
} from '../../constants/constants';
import { SignRequest } from '../../models/sign-request';
import { SignRequestFilterFormModel } from '../../models/sign-request-filter-form-model';
import { SignRequestStatus } from '../../models/sign-request-status';
import { selectSelectedSignRequestIds } from '../../store/sign-requests-slice';
import SignRequestsHeaderFilterForm from './sign-requests-header-filter-form/sign-requests-header-filter-form';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';

interface Props {
  pagination: PaginationProps;
  templateFields: TemplateField[];
  unarchivedSignRequests: SignRequest[];
}

const SignRequestsHeader = ({ pagination, templateFields, unarchivedSignRequests }: Props): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const screens = useBreakpoint();
  const initialValues: SignRequestFilterFormModel = useMemo(() => {
    return {
      name: '',
      clientUser: '',
      unit: '',
      customerId: '',
      status: SignRequestStatus.Signed,
      archived: '',
    };
  }, []);
  const { data: userSettings } = useGetUserSettingsQuery();
  const form = useFormWithSearchParams<SignRequestFilterFormModel>(
    initialValues,
    userSettings?.[UserSettings.DateFormat],
  );
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isTemplateSelected = !!(searchParams.get('template') && templateFields.length > 0);
  const [isTemplateFieldsVisible, setIsTemplateFieldsVisible] = useState(true);
  const hasPermissionArchiveDocuments = useHasPermission(UserPermissionType.ArchiveDocuments);
  const selectedSignRequests = useAppSelector(selectSelectedSignRequestIds);

  const handleOpenModal = (modalId: string) => {
    dispatch(openModal(modalId));
  };

  const openArchiveConfirmationModal = () => dispatch(openModal(signRequestArchiveModalId));

  const hasSelectedUnarchivedRequests = useMemo(
    () => unarchivedSignRequests.some(signRequest => selectedSignRequests.includes(signRequest.id)),
    [selectedSignRequests, unarchivedSignRequests],
  );

  return (
    <>
      {screens.sm ? (
        <div>
          <PageTitle>{t('menu.signRequests', { ns: 'common' })}</PageTitle>
          <SignRequestsHeaderFilterForm
            form={form}
            visibleTemplateFields={isTemplateFieldsVisible && isTemplateSelected}
            templateFields={templateFields}
            initialValues={initialValues}
          />
          <Row className="paginationNoWrap" gutter={[18, 18]}>
            <Col>
              <Row gutter={[18, 18]}>
                <Col>
                  <FormActions form={form} formId={signRequestFilterFormId} />
                </Col>
                <Col>
                  <CustomButton type="primaryLight" onClick={() => handleOpenModal(signRequestsExportModalId)}>
                    {t('action.export', { ns: 'common' })}
                  </CustomButton>
                </Col>
                {isTemplateSelected && (
                  <Col>
                    <CustomButton onClick={() => setIsTemplateFieldsVisible(!isTemplateFieldsVisible)} type="primary">
                      {isTemplateFieldsVisible
                        ? t('signRequestsHeader.labels.simpleSearch')
                        : t('signRequestsHeader.labels.advancedSearch')}
                    </CustomButton>
                  </Col>
                )}
                {hasSelectedUnarchivedRequests && (
                  <Col>
                    <CustomButton
                      type="darkBlue"
                      disabled={!hasPermissionArchiveDocuments}
                      onClick={openArchiveConfirmationModal}
                    >
                      {t('signScanRequest.action.uploadToServer', { ns: 'common' })}
                    </CustomButton>
                  </Col>
                )}
              </Row>
            </Col>
            <Col flex="auto"></Col>
            <Col>
              <Button onClick={() => handleOpenModal(columnsSelectorModalId)} type="primary">
                {t('signScanRequest.label.columnsFilter', { ns: 'common' })} <ColumnsIcon />
              </Button>
            </Col>
            <Col flex="auto" className="pagination-col">
              <Pagination {...pagination} />
            </Col>
          </Row>
        </div>
      ) : (
        <>
          <CollapsableFilter
            header={<Typography.Text strong>{t('label.filters', { ns: 'common' })}</Typography.Text>}
            keyProp="1"
          >
            <SignRequestsHeaderFilterForm form={form} initialValues={initialValues} />
            <Row justify="space-between">
              <Col flex="auto">
                <FormActions form={form} formId={signRequestFilterFormId} />
              </Col>
            </Row>
          </CollapsableFilter>
          <div className="paginationPadding">
            <Pagination {...pagination} />
          </div>
        </>
      )}
    </>
  );
};
export default SignRequestsHeader;
